/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
/* eslint-disable id-length */
import React, { useEffect, useState, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import lottie from 'lottie-web';
import { toast } from 'react-toastify';
import { TableUtility } from '@utility/dynamic-table';
import { useAuth } from '../../../Hooks/useAuth';
import UseAxios from '../../../Api/http/http';
import emptyStateAnimationData from '../../../Assets/images/json/dar_no_data.json';
import {
  buildSalaryReportApiUrl,
  getSalaryReportFilterOptions,
} from '../../../Api/request/salaryReport';
import { SALARY_REPORT } from '../../../Api/resources/endPoint';
import './ProjectWiseSalaryReport.scss';

const PAGE_TITLE = 'Project Wise Salary Report';
const SALARY_REPORT_BASE_URL = buildSalaryReportApiUrl(SALARY_REPORT);
const getStoredAuthToken = () => localStorage.getItem('authToken') || '';

const EmptyState = ({ message }) => {
  const animationContainer = useRef(null);

  useEffect(() => {
    if (!animationContainer.current) return undefined;
    const animation = lottie.loadAnimation({
      container: animationContainer.current,
      renderer: 'svg',
      loop: true,
      autoplay: true,
      animationData: emptyStateAnimationData,
    });
    return () => animation.destroy();
  }, []);

  return (
    <div className="salary-report-empty-state">
      <div className="salary-report-empty-state-card">
        <div ref={animationContainer} className="salary-report-empty-state-animation" />
        <p className="salary-report-empty-state-text">{message}</p>
      </div>
    </div>
  );
};
EmptyState.propTypes = { message: PropTypes.string.isRequired };

const mapFilterOptions = (opts = []) =>
  opts.map((opt) => ({
    label: String(opt.label ?? opt.value ?? ''),
    value: String(opt.value ?? ''),
  }));

const hasFilterValue = (item) => {
  if (!item || item?.type !== 'filter') return false;
  if (Array.isArray(item?.selectedValues) && item.selectedValues.length > 0) return true;
  if (Array.isArray(item?.range)) return item.range.some(Boolean);
  return Boolean(item?.selectedValue ?? item?.inputValue ?? item?.value);
};

const getAppliedFiltersFromConfiguration = (tableConfiguration = []) =>
  tableConfiguration
    .flatMap((column) => column?.menuItems || [])
    .flatMap((menuItem) => menuItem?.items || [])
    .filter(hasFilterValue);

const ProjectWiseSalaryReport = () => {
  const { userProfile, user } = useAuth();
  const authToken = userProfile?.access_token || getStoredAuthToken();

  const userInfo = useMemo(() => ({
    id: userProfile?.employeeId,
    emailId: userProfile?.email || '',
    employeeId: userProfile?.employeeId || '',
    permissions: userProfile?.permissions || {},
    roleDetails: Array.isArray(userProfile?.roleDetails) ? userProfile.roleDetails : [],
    globalColors: userProfile?.siteConfiguration?.global_colors,
  }), [userProfile]);

  const [filterColumns, setFilterColumns] = useState([]);
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [filtersLoaded, setFiltersLoaded] = useState(false);

  // NEW: State to store the total employee count
  const [employeeCount, setEmployeeCount] = useState(0);

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const response = await UseAxios(getSalaryReportFilterOptions());
        const { verticals = [] } = response?.data?.data || {};

        const buildDropdown = (field, label, opts, order) => {
          const optionsData = mapFilterOptions(opts);
          return {
            field,
            header: label,
            filter: true,
            isGlobalFilter: true,
            visible: false,
            sortable: false,
            pinnable: false,
            order,
            showFilterButtonInHeader: false,
            menuItems: [{
              label,
              items: [{
                type: 'filter',
                field,
                label,
                template: 'multiSelect',
                options: optionsData,
                values: optionsData,
                selectedValues: [],
                selectedValue: null,
              }],
            }],
          };
        };

        const STATUS_OPTS = [
          { label: 'Active', value: 'Y' },
          { label: 'Inactive', value: 'N' },
          { label: 'Resigned', value: 'R' },
        ];

        setFilterColumns([
          {
            field: 'filter_monthYearRange',
            header: 'Month',
            visible: false,
            isGlobalFilter: true,
            showFilterButtonInHeader: false,
            type: 'monthWiseCalendar',
            filterVisible: true,
            order: -1,
            menuItems: [
              {
                label: 'Month',
                items: [
                  {
                    type: 'filter',
                    field: 'filter_monthYearRange',
                    template: 'monthWiseCalendar',
                    range: [null, null],
                    rangePlaceholders: ['From', 'To'],
                    url: `${SALARY_REPORT_BASE_URL}/filters/monthYearRange`,
                    isMandatory: true,
                    requiredOnMainApply: true,
                    requiredMessage: 'Please select both From and To Month to view the report.',
                  },
                ],
              },
            ],
          },
          buildDropdown('vertical', 'Employee Vertical', verticals, 1),
          buildDropdown('employeeStatus', 'Employee Status', STATUS_OPTS, 2),
        ]);
      } catch (error) {
        toast.error('Failed to load filter options');
      } finally {
        setFiltersLoaded(true);
      }
    };
    if (authToken) fetchFilters();
  }, [authToken]);

  const configuration = useMemo(() => ({
    generalSettings: {
      isModifiedFilter: true,
      enableApiFiltering: true,
      fetchOnMount: true,
      preventInitialFetch: false,
      loadOnMount: true,
      initialApiCall: true,
      baseURL: SALARY_REPORT_BASE_URL,
      tableUtilityConfig: { isModifiedFilter: true },
      headers: { token: authToken },
      userInfo: user,
      additionalPayloadData: {
        filter: appliedFilters,
        pagination: { limit: 10000, page: 1 },
      },
      apiEndpoints: {
        getFilteredData: { endpoint: '', enabled: true },
        getAllViews: { endpoint: '', enabled: false },
        saveOrUpdateView: { endpoint: '', enabled: false },
        deleteView: { endpoint: '', enabled: false },
        download: {
          endpoint: 'export',
          enabled: true,
          filename: 'Project_Wise_Salary_Report',
          sheetName: 'Salary Report',
        },
      },
      showToolBar: true,
      showPagination: true,
      handler: {
        onFilterUpdate: ({ action, filters }) => {
          const safeFilters = Array.isArray(filters) ? filters : [];
          if (action === 'apply') {
            const monthFilter = safeFilters.find((f) => f.field === 'filter_monthYearRange');
            const hasFrom = monthFilter?.range?.[0];
            const hasTo = monthFilter?.range?.[1];
            if (!hasFrom || !hasTo) {
              toast.error('Please select both From and To Month before applying.');
              return;
            }
            setAppliedFilters(safeFilters);
            setIsFilterApplied(true);
          } else if (action === 'reset') {
            setAppliedFilters([]);
            setIsFilterApplied(false);
            setEmployeeCount(0); // NEW: Reset the count when table resets
          }
        },
        handleFilteredApiResponse: (response) => {
          const tableData = response?.data?.tableData || [];
          setEmployeeCount(response?.data?.employeeCount || 0); // NEW: Extract the total employee count from response

          if (Array.isArray(response?.data?.tableConfiguration) && filterColumns.length > 0) {
            const finalConfig = response.data.tableConfiguration.map((beCol) => {
              const col = { ...beCol };
              col.align = col.numeric ? 'right' : 'left';
              if (col.field === 'name') {
                col.className = 'salary-report-sticky-column';
                col.headerClassName = 'salary-report-sticky-column';
              }
              return col;
            });

            filterColumns.forEach((pCol) => {
              const existingIdx = finalConfig.findIndex(index => index.field === pCol.field);
              const safeAppliedFilters = Array.isArray(appliedFilters) ? appliedFilters : [];
              const activeFilter = safeAppliedFilters.find((val) => val.field === pCol.field);
              let mergedMenuItems = pCol.menuItems;

              if (activeFilter) {
                mergedMenuItems = pCol.menuItems?.map((menu) => ({
                  ...menu,
                  items: menu.items?.map((item) => ({ ...item, ...activeFilter })),
                }));
              }

              if (existingIdx >= 0) {
                finalConfig[existingIdx] = {
                  ...finalConfig[existingIdx],
                  isGlobalFilter: true,
                  menuItems: mergedMenuItems,
                  filter: true,
                  showFilterButtonInHeader: false,
                };
              } else {
                finalConfig.push({ ...pCol, menuItems: mergedMenuItems });
              }
            });
            response.data.tableConfiguration = finalConfig;
          }

          const dummyPagination = { page: 1, limit: 10000, total: tableData.length, totalPages: 1 };
          if (response) {
            response.meta = response.meta || dummyPagination;
            response.pagination = response.pagination || dummyPagination;
            if (response.data) {
              response.data.showExpandCollapse = false;
              response.data.enableTreeTable = false;
              response.data.meta = response.data.meta || dummyPagination;
              response.data.pagination = response.data.pagination || dummyPagination;
            }
          }
          return response;
        },
        setTableStateHandler: (getTableState) => {
          const tableState = getTableState?.() || {};
          const configFilters = getAppliedFiltersFromConfiguration(
            Array.isArray(tableState.tableConfiguration) ? tableState.tableConfiguration : [],
          );
          const stateFilters = Array.isArray(tableState.filters)
            ? tableState.filters.filter(hasFilterValue)
            : [];
          const mergedFilters = stateFilters.length > 0 ? stateFilters : configFilters;
          const safeMergedFilters = Array.isArray(mergedFilters) ? mergedFilters : [];
          const nextHasApplied = safeMergedFilters.some(
            (f) => f.field === 'filter_monthYearRange' && f.range && f.range[0] && f.range[1],
          );
          setIsFilterApplied((prev) => (prev === nextHasApplied ? prev : nextHasApplied));
        },
      },
      toolBarSettings: {
        showToolBarHeading: true,
        toolBarHeading: PAGE_TITLE,
        showColumnFilterMenu: true,
        showDownloadExcel: false,
        showDownloadCsv: true,
        showAddNew: false,
        showExpandCollapse: false,
        showColumnCustomize: false,
        showHelp: false,
        showSearch: false,
        showSaveView: false,
        showReset: false,
      },
    },
    tableSettings: {
      enableTreeTable: false,
      size: 'small',
      loaderSkeleton: { rows: 5, bodyCells: 1, columns: 8 },
      pageSize: 10000,
      showPageSizeSelector: false,
      paginationConfig: { page: 1, limit: 10000, rowsPerPageOptions: [10000] },
      showExpandCollapse: false,
      tableConfig: filterColumns,
      maxFrozenColumns: { desktop: 4, tablet: 2, mobile: 1 },
      tableClassName: 'custom-table-salary-report',
    },
  }), [filterColumns, appliedFilters, authToken, userInfo, user]);

  return (
    <div className="salary-report-page filter-class">
      {filtersLoaded && (
        <>
          <TableUtility
            configuration={configuration}
            emptyStateComponent={
              !isFilterApplied ? (
                <EmptyState message="Select From Month and To Month to view the report" />
              ) : undefined
            }
          />
          {/* NEW: Display the Employee Count at the bottom */}
          {isFilterApplied && (
            <div className="salary-report-footer">
              <span className="salary-report-total-count">Total Employee : {employeeCount}</span>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ProjectWiseSalaryReport;
